import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { DisplayProps, SpaceProps } from 'styled-system';
import { display, space, themeGet } from 'styled-system';

import { useLocalization } from '../../providers/LocaleProvider';
import { focusOutlineCSSWithOffset } from '../../theme/styles';
import type { WhiteSpaceProps } from '../../theme/system-utilities';
import { whiteSpace } from '../../theme/system-utilities';
import { HomeClubLegalText } from '../HomeClubLegalText';
import type { LinkStylesProps } from '../Link';
import { linkStyles } from '../Link';
import {
  BodyRegularBR2,
  TYPOGRAPHY_BR2_FONT_SIZE,
  TYPOGRAPHY_BR2_LINE_HEIGHT,
} from '../Typography';

interface RegistrationLegalTermsProps extends DisplayProps, SpaceProps {
  withHomeClubLegalTerms: boolean;
}

const UnstyledContainer = styled.div<any>`
  ${display};
  ${space};
`;

const Container = (props: ComponentProps<typeof UnstyledContainer>) => (
  <UnstyledContainer mt={['sp_20', null, 'sp_24', 'sp_32']} {...props} />
);

const UnstyledStyledText = styled(BodyRegularBR2)<WhiteSpaceProps & LinkStylesProps>`
  ${display};
  ${whiteSpace};

  a {
    ${linkStyles};
    ${focusOutlineCSSWithOffset};
    border-radius: ${themeGet('radii.small')};
    color: ${themeGet('colors.sys.neutral.border.strong')};
    text-decoration: underline;
  }
`;

const StyledText = (props: ComponentProps<typeof UnstyledStyledText>) => (
  <UnstyledStyledText textAlign="left" whiteSpace="pre-line" styled={true} {...props} />
);

const StyledHomeClubLegalText = styled(HomeClubLegalText)`
  &:before {
    content: '**';
  }
`;

const RegistrationLegalTerms = ({
  withHomeClubLegalTerms,
  ...customProps
}: RegistrationLegalTermsProps) => {
  const { prefix, r, t } = useLocalization();

  return (
    <Container {...customProps}>
      <StyledText
        as="p"
        mt="sp_0"
        mb="sp_0"
        dangerouslySetInnerHTML={{
          __html: r.t('h24_registration_page_legal_terms', {
            datenschutz_href: prefix(t('h24_datenschutz_link_url')),
          }),
        }}
      />
      {withHomeClubLegalTerms && (
        <StyledHomeClubLegalText
          mt="sp_16"
          fontSize={TYPOGRAPHY_BR2_FONT_SIZE}
          lineHeight={TYPOGRAPHY_BR2_LINE_HEIGHT}
        />
      )}
    </Container>
  );
};

export default RegistrationLegalTerms;
